import { SegmentPill } from 'components/ui/overlay-motion';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { LayoutGroup } from 'motion/react';
import { useId } from 'react';

type Props<Value extends string> = {
    /** The choices, in order: a field's conditions, a text filter's match, a composite part's options. */
    items: { value: Value; label: string }[];
    value: Value;
    onChange: (value: Value) => void;
    'aria-label'?: string;
};

/** One choice out of a few, as segments over which one pill slides to the chosen one. */
export default function ConditionSwitch<Value extends string>({
    items,
    value,
    onChange,
    'aria-label': ariaLabel,
}: Props<Value>) {
    const pillGroup = useId();
    return (
        <LayoutGroup id={pillGroup}>
            <ToggleGroup
                aria-label={ariaLabel}
                value={[value]}
                onValueChange={(next: string[]) => {
                    // Pressing the chosen segment empties the group; the switch always holds a choice.
                    if (next[0]) onChange(next[0] as Value);
                }}
                spacing={0.5}
                // `isolate` keeps the pill above the track and under every label. A focused segment stays at `z-auto`:
                // raised, it would lift its pill over the labels the pill slides across.
                className="bg-segmented isolate rounded-[9px] p-[3px]"
            >
                {items.map((item) => (
                    <ToggleGroupItem
                        key={item.value}
                        value={item.value}
                        className="text-foreground-tertiary data-pressed:text-foreground text-meta relative h-[26px] rounded-sm px-2.5 font-medium hover:bg-transparent focus:z-auto focus-visible:z-auto aria-pressed:bg-transparent"
                    >
                        {item.value === value ? <SegmentPill className="bg-paper" /> : null}
                        {item.label}
                    </ToggleGroupItem>
                ))}
            </ToggleGroup>
        </LayoutGroup>
    );
}
